import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { profile } from "../../data/portfolioData";
import { scrollToSection } from "./shared";

const links = [
  { id: "work", label: "Work" },
  { id: "experience", label: "Experience" },
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2.5 bg-[#07090E]/80 backdrop-blur-xl border-b border-white/[0.06]" : "py-5 bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          className="font-code text-lg font-semibold text-slate-100 tracking-tight"
          data-testid="nav-logo"
          aria-label="Go to top"
        >
          {profile.initials}<span className="text-amber-400">.</span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-sm text-slate-400 hover:text-slate-100 transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative group">
            <button
              className="inline-flex items-center gap-1.5 text-xs font-code text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="nav-resume-dropdown-toggle"
              aria-haspopup="true"
            >
              <Download size={13} /> Resume <span className="text-[9px] text-slate-600 transition-transform duration-200 group-hover:rotate-180">▾</span>
            </button>
            <div
              className="invisible absolute right-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
              data-testid="nav-resume-dropdown"
            >
              <div className="w-52 rounded-xl border border-white/10 bg-[#0F1420]/95 backdrop-blur-xl p-1.5 shadow-2xl">
                {profile.resumeVariants.map((r) => (
                  <a
                    key={r.label}
                    href={r.url}
                    download
                    className="flex items-center justify-between rounded-lg px-3 py-2.5 text-xs text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-amber-300"
                    data-testid={`nav-resume-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {r.label} <Download size={12} className="opacity-50" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={() => go("contact")}
            className="rounded-full bg-amber-400 px-5 py-2 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)]"
            data-testid="nav-cta-talk"
          >
            Let's Talk
          </button>
        </div>

        <button
          className="md:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0D15]/95 backdrop-blur-xl border-b border-white/[0.06]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left text-lg font-display text-slate-200"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-3 border-t border-white/[0.06]">
                <button
                  onClick={() => go("contact")}
                  className="rounded-full bg-amber-400 px-6 py-2.5 text-sm font-semibold text-[#07090E]"
                  data-testid="nav-mobile-cta-talk"
                >
                  Let's Talk
                </button>
                <div className="mt-4 flex flex-wrap gap-2">
                  {profile.resumeVariants.map((r) => (
                    <a
                      key={r.label}
                      href={r.url}
                      download
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-[11px] font-code text-slate-400"
                      data-testid={`nav-mobile-resume-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Download size={11} /> {r.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
